import React from "react";
import { Sparkles, MessageSquareQuote, SlidersHorizontal, Globe2, Clock, Waves, Inbox, HandHeart, ListChecks, ShieldCheck } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, StatBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { NeedCardMock, RefineMock, UrgencyMock, AudienceMock, PrefsMock, ProfileFieldsMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1511376979163-f804dff7ad7b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function OtterAssistFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-otter-assist-page">
      <FeatureHero
        eyebrow="Otter Assist"
        icon={Sparkles}
        title="Stop hoping the right person sees your post."
        copy="Tell Startup Otter what you need and let Otter Assist help identify people across the community who may actually be able to help."
        ctaLabel="Discover Otter Assist"
        ctaTo="/register"
        visual={<Shot src="/features/otter-assist-needs.png" alt="Community Needs delivered by Otter Assist, showing why you matched and the I Can Help action" url="startupotter.com/dashboard/otter-assist" testId="otter-assist-hero-visual" priority />}
      />

      <IconGrid
        testId="oa-eligible-needs"
        tone="tint"
        columns={3}
        title="Start with a real need."
        intro="Activate Otter Assist on eligible Requests, Questions, and Work posts when you'd like help finding the right people."
        items={[
          { icon: HandHeart, title: "Request", copy: "You're looking for something specific and would rather not guess who to ask." },
          { icon: MessageSquareQuote, title: "Question", copy: "You need an answer from someone who has genuinely been there." },
          { icon: ListChecks, title: "Work", copy: "A role, a project, or a hand on something that needs doing." },
        ]}
      />

      <FeatureRow
        testId="oa-understanding"
        tone="dark"
        label="Understanding"
        icon={Sparkles}
        title="Let Otter understand the ask."
        copy="Otter Assist creates a need summary and evaluates potential matches based on their overall relevance to what you need."
        visual={
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="mock-need-summary">
            <div className="flex items-center gap-2 text-sm font-[640] text-teal-300">
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Need summary
            </div>
            <p className="mt-3 text-sm leading-6 text-white/85">
              A fractional finance operator with climate hardware runway experience, comfortable modelling
              scenarios ahead of a board review.
            </p>
            <div className="mt-5 space-y-3">
              {[["Overall relevance", 88], ["Related experience", 74], ["Recent activity", 61]].map(([label, pct]) => (
                <div key={label}>
                  <div className="flex items-center justify-between text-xs text-white/60"><span>{label}</span></div>
                  <div className="mt-1 h-2 w-full rounded-full bg-white/10"><div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-5 text-white/50">Relevance is a judgement about overall fit — not a keyword count.</p>
          </div>
        }
      />

      <FeatureRow
        testId="oa-refinement"
        label="Refinement"
        icon={SlidersHorizontal}
        title="Give Otter a few clues."
        copy="Refine matching by Industry, Region, and Work Setting—and mark preferences as Preferred or Required."
        visual={<RefineMock testId="mock-oa-refine" />}
      />

      <FeatureRow
        testId="oa-audience"
        tone="tint"
        flip
        label="Audience"
        icon={Globe2}
        title="Decide how far to swim."
        copy="Search your Network, extend beyond it, or reach into the wider eligible Startup Otter community."
        visual={<AudienceMock testId="mock-oa-audience" />}
      />

      <FeatureRow
        testId="oa-urgency"
        label="Timing"
        icon={Clock}
        title="Tell people when it matters."
        copy="Choose Immediate, ASAP, Soon, Within X Days, or Flexible so potential helpers understand the timing."
        visual={<UrgencyMock testId="mock-oa-urgency" />}
      />

      <FeatureRow
        testId="oa-waves"
        tone="dark"
        flip
        label="Outreach"
        icon={Waves}
        title="Reach the right people—not everyone."
        copy="Outreach expands progressively in waves of 5, then 10, then 15 instead of blasting the entire community at once."
        visual={
          <div className="grid grid-cols-3 gap-3" data-testid="mock-oa-waves">
            {[{ n: 5, l: "Wave 1" }, { n: 10, l: "Wave 2" }, { n: 15, l: "Wave 3" }].map((w, i) => (
              <div key={w.l} className={`rounded-2xl p-6 text-center ${i === 0 ? "bg-primary text-primary-foreground" : "border border-white/10 bg-white/[0.04] text-white"}`}>
                <div className="text-4xl font-[700]">{w.n}</div>
                <div className={`mt-1 text-xs font-[560] ${i === 0 ? "text-primary-foreground/80" : "text-white/60"}`}>{w.l}</div>
              </div>
            ))}
          </div>
        }
      />

      <PhotoBand
        testId="oa-human"
        src={PHOTO}
        alt="Two people in conversation across a table"
        kicker="Community Needs"
        title="Relevant needs come to you."
        copy="Matched members can review opportunities in Community Needs and decide whether they're a fit — then answer with one simple response: I Can Help."
      />

      <FeatureRow
        testId="oa-community-needs"
        tone="tint"
        label="Your inbox"
        icon={Inbox}
        title="A short list, not a firehose."
        copy="Needs arrive with a plain-English reason you matched, the timing, and nothing else to wade through."
        visual={<NeedCardMock testId="mock-oa-community-needs" />}
      />

      <IconGrid
        testId="oa-responses"
        tone="brand"
        columns={3}
        title="One simple response: I Can Help."
        intro="Members can volunteer to help without forced introductions or unwanted conversations."
        items={[
          { icon: HandHeart, title: "I Can Help", copy: "Volunteer, and optionally add a short note about how." },
          { icon: ShieldCheck, title: "Not a good fit", copy: "Pass quietly. Nobody is told you said no." },
          { icon: Clock, title: "Not right now", copy: "Keep it out of the way and get on with your day." },
        ]}
      />

      <FeatureRow
        testId="oa-my-needs"
        label="My Needs"
        icon={ListChecks}
        title="Keep every need organized."
        copy="Track active needs, outreach progress, responders, urgency, status, and next steps from My Needs."
        visual={<ProfileFieldsMock title="My Needs" testId="mock-oa-my-needs" items={[["Looking for a fractional finance operator", "Awaiting your decision · Soon — within 7 days"], ["Responders", "3 offered to help"], ["Assists left this cycle", "72 of 75"]]} />}
      />

      <FeatureRow
        testId="oa-recipient-controls"
        tone="tint"
        flip
        label="Your controls"
        icon={ShieldCheck}
        title="You control what reaches you."
        copy="Manage matching preferences, digest frequency, preferred industries and regions, pauses, daily limits, and notification settings."
        visual={<PrefsMock testId="mock-oa-prefs" />}
      />

      <StatBand
        testId="oa-stats"
        items={[
          { value: "5 → 10 → 15", label: "Outreach widens in waves, only if the need still calls for it." },
          { value: "Preferred / Required", label: "Say what would be nice and what is non-negotiable." },
          { value: "PRO & CAPITAL", label: "Otter Assist is included, with a set number of Assists each cycle." },
        ]}
      />

      <ClosingCta
        testId="otter-assist-closing"
        title="AI that helps people find people."
        copy="Otter Assist does the searching. Humans create the relationships."
        secondary="The AI does the swimming. Humans make the connection."
        ctaLabel="Try Otter Assist"
        ctaTo="/register"
      />

      <FeatureFooterNav next={{ to: "/features/analytics", label: "Analytics", desc: "See how people discover, engage with, and act on everything you manage." }} />
    </div>
  );
}
